import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBug, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { IssueTypeEnum, IssueStatusEnum, generateIssueCode, getIssueTypeByValue, getIssueStatusByValue } from '../enums/IssueEnums.js';
import StatusBadge from './StatusBadge.js';
import '../common/modals.css';
import './IssueViewModal.css';

/**
 * Simple view-only modal for displaying issue details
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.issue - Issue object to display
 */
const IssueViewModal = ({ isOpen, onClose, issue }) => {
  if (!isOpen || !issue) return null;

  const issueType = getIssueTypeByValue(issue.type);
  const issueStatus = getIssueStatusByValue(issue.status);
  const issueCode = generateIssueCode(issue.issueNumber);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTypeIcon = () => {
    if (issue.type === IssueTypeEnum.BUG.value) {
      return faBug;
    } else if (issue.type === IssueTypeEnum.FEATURE.value) {
      return faLightbulb;
    }
    return null;
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-container modal-animated issue-view-modal">
        <div className="modal-header">
          <h2>{issueCode}</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-content">
          <div className="issue-view-field">
            <label className="issue-view-label">Type:</label>
            <div className="issue-view-value">
              {getTypeIcon() && (
                <FontAwesomeIcon 
                  icon={getTypeIcon()} 
                  className="issue-type-icon"
                  style={{ color: '#666' }}
                />
              )}
              <span>{issueType?.displayName || issue.type}</span>
            </div>
          </div>

          <div className="issue-view-field">
            <label className="issue-view-label">Status:</label>
            <div className="issue-view-value">
              {issueStatus ? (
                <StatusBadge
                  label={issueStatus.displayName}
                  colorFamily={issueStatus.colorFamily || 'gray'}
                  size="normal"
                />
              ) : (
                <span>{issue.status}</span>
              )}
            </div>
          </div>

          <div className="issue-view-field">
            <label className="issue-view-label">Description:</label>
            <div className="issue-view-value issue-description">
              {issue.description || 'No description provided'}
            </div>
          </div>

          <div className="issue-view-field">
            <label className="issue-view-label">Created:</label>
            <div className="issue-view-value">
              {issue.createdAt ? new Date(issue.createdAt).toLocaleString() : '-'}
            </div>
          </div>

          {issue.completedAt && (
            <div className="issue-view-field">
              <label className="issue-view-label">Completed:</label>
              <div className="issue-view-value">
                {new Date(issue.completedAt).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssueViewModal;
